import VerifiedIcon from "@mui/icons-material/Verified";
import { Box } from "@mui/material";
import { GridSortItem } from "@mui/x-data-grid";
import { useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import CustomDataGridV2 from "../../../../components/CustomDataGridV2";
import Flex from "../../../../components/Flex";
import SuccessModal, { useSuccess } from "../../../../components/SuccessModal";
import Text from "../../../../components/Text";
import ToggleSwitch from "../../../../components/ToggleSwitch";
import ActionMenu from "../../../../components/app/ActionMenu";
import DeleteModal from "../../../../components/app/DeleteModal";
import UpsertStaffUserDrawer from "../../../../components/app/admin/provider-groups/UpsertStaffUserDrawer";
import UserProfileDrawer from "../../../../components/app/settings/user-settings/UserProfileDrawer";
import { GENDER_RECORD } from "../../../../constants";
import { StaffUser } from "../../../../constants/staff-user-schema";
import { UserData } from "../../../../dummy-data/user-data";
import useDisclosure from "../../../../hooks/use-disclosure";
import { useArchiveState } from "../../../../hooks/useArchiveState";
import { useSnackbar } from "../../../../hooks/useSnackbar";
import { useUserArchive } from "../../../../queries/use-archive-user";
import { useGetUserDetail } from "../../../../queries/use-get-pertiCular-user";
import { useUsersList } from "../../../../queries/use-get-users";
import { useResendLink } from "../../../../queries/use-resend-link";
import { useStatusUsers } from "../../../../queries/use-status-users";
import { useUpdateProviderUserStatus } from "../../../../queries/use-update-provider-status";
import { useUpdateUser } from "../../../../queries/use-update-user";
import { formatDateTime } from "../../../../utils/date-time-utility";
import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_VALUE_LABEL_LIST,
  DGT_Pagination,
  applyDefaultColumnSettings,
  formatPhoneNumber,
  getErrorSnackbar,
  getUnNecessaryTransformData,
  transformMultiSelectFrontEndVal,
} from "../../../../utils/utils";
import { UserCardData } from "../../../../types/user-types";

const ProviderGroupStaffUsers = () => {
  const params = useParams();

  const { searchKey }: any = useOutletContext();

  const [pagination, setPagination] = useState<DGT_Pagination>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const { mutateAsync: statusChange } = useStatusUsers();

  const [selectedUserId, setSelectedUserId] = useState<string>();
  const { data: userDetail }: any = useGetUserDetail({
    userId: selectedUserId,
    xTENANTID: params?.providerGroupId,
    disable: !selectedUserId,
  });

  const {
    opened: openedStaffUserModal,
    open: openStaffUserModal,
    close: closeStaffUserModal,
  } = useDisclosure(false);
  const {
    data: UsersList,
    isFetching,
    refetch,
    isLoading: tableDataLoading,
  } = useUsersList({
    gridSorting,
    pagination,
    searchKey,
    providerGroupId: params?.providerGroupId,
  });
  const [openedResendLink, { open: openResendLink, close: closeResendLink }] =
    useSuccess();
  const { mutateAsync: updateStaffUser } = useUpdateUser();
  const [archive, setArchive] = useState(false);
  const { mutateAsync: useArchiveUser } = useUserArchive();
  const { deleteDisc, archiveHandler } = useArchiveState(useArchiveUser);
  const { mutateAsync: providerStatusUpdate } = useUpdateProviderUserStatus();
  const [selectedUserData, setSelectedUserData] = useState<UserCardData | null>(
    null
  );
  const showSnackbar = useSnackbar();
  const { mutateAsync: resendInvitationLink } = useResendLink();

  const staffUserDisclosure = useDisclosure(false, {
    onClose: () => setSelectedUserData(null),
  });

  const editStaffUser = (values: StaffUser) => {
    const transFormData: any = getUnNecessaryTransformData(
      ["workLocations"],
      values
    );
    return updateStaffUser({
      user: transFormData,
      xTENANTID: params?.providerGroupId,
    });
  };
  function editUserHandler(values: any) {
    setSelectedUserId(values?.uuid);
    setSelectedUserData(values);
    openStaffUserModal();
  }

  function toggleStatus<T>(value: boolean, id: T, role: string) {
    if (role === "PROVIDER") {
      return providerStatusUpdate({
        providerId: id?.toString() ?? "",
        status: value,
        xTENANTID: params.providerGroupId,
      }).then((res) => {
        refetch();
        return res;
      });
    }

    return statusChange({
      userId: id?.toString() ?? "",
      status: value,
      xTENANTID: params.providerGroupId,
    }).then((res) => {
      refetch();
      return res;
    });
  }

  const COLUMNS = applyDefaultColumnSettings<UserData>([
    {
      // todo change while backend accept sort key
      field: "userInfo",
      flex: 0.9,
      minWidth: 200,
      disableColumnMenu: true,
      headerName: "Name",
      // todo need to fix type
      renderCell: ({ row }: any) => {
        return (
          <Box
            sx={{
              cursor: "pointer",
            }}
            onClick={() => {
              setSelectedUserData(row);
              staffUserDisclosure.open();
            }}
          >
            <Flex gap={0.6}>
              <Text
                marginBottom={"3px"}
                color={"primary.main"}
                fontWeight={"medium"}
              >
                {`${row?.firstName} ${row.lastName}`}
              </Text>

              <Text variant="subtitle2">
                {`(${GENDER_RECORD[row?.gender]})`}
              </Text>
            </Flex>
          </Box>
        );
      },
      sortable: false,
    },
    {
      field: "role",
      flex: 0.5,
      minWidth: 130,
      headerName: "Role",
      sortable: false,
      renderCell: ({ value }) => {
        return <Text>{value ?? "-"}</Text>;
      },
    },
    {
      field: "phone",
      flex: 0.6,
      minWidth: 140,
      headerName: "Contact",
      sortable: false,
      renderCell: ({ ...params }) => {
        return (
          <Text>{params.value ? formatPhoneNumber(params.value) : "-"}</Text>
        );
      },
    },
    {
      field: "email",
      flex: 0.9,
      minWidth: 240,
      headerName: "Email",
      renderCell: ({ row, value }) => (
        <Flex alignItems={"center"} gap={"4px"}>
          {value ? (
            <>
              <VerifiedIcon
                color={row.emailVerified ? "success" : "disabled"}
                fontSize="small"
              />
              <Text>{value}</Text>
            </>
          ) : (
            <Text>-</Text>
          )}
        </Flex>
      ),
      sortable: false,
    },
    {
      field: "lastLogin",
      flex: 0.7,
      minWidth: 150,
      headerName: "Last Sign In",
      sortable: false,
      renderCell: ({ ...params }) => {
        return <Text>{params.value ? formatDateTime(params.value) : "-"}</Text>;
      },
    },
    {
      field: "active",
      headerName: "Status",
      width: 150,
      renderCell: ({ row, value }) => (
        <ToggleSwitch
          id={row.uuid.toString()}
          checked={value}
          onConfirm={(v, id) => toggleStatus(v, id, row?.roleType)}
        />
      ),
      sortable: false,
    },
    {
      field: "Action",
      width: 90,
      // todo need to fix type
      renderCell: ({ row }: any) => {
        return (
          <ActionMenu
            options={[
              {
                value: "resendLink",
                label: "Resend Link",
                isDisabled: row?.emailVerified,
                hoverMsg: row?.emailVerified
                  ? "Provider Already Verified"
                  : undefined,
              },
              { value: "edit", label: "Edit" },
              {
                value: "archive",
                label: row?.archive ? "Unarchive" : "Archive",
                isDisabled: row?.active,
                hoverMsg: "Make status disable for archive",
              },
            ]}
            onActionClick={(v) => {
              if (v === "archive") {
                setArchive(row?.archive);
                deleteDisc.open(row?.uuid);
              } else if (v === "edit" || v === "resendLink") {
                if (v === "edit") {
                  editUserHandler(row);
                } else {
                  resendInvitationLink({
                    linkType: "set",
                    setPasswordRequest: {
                      email: row.email,
                    },
                    xTENANTID: params.providerGroupId,
                  })
                    .then(openResendLink)
                    .catch((err) => {
                      showSnackbar(getErrorSnackbar(err));
                    });
                }
              }
            }}
          />
        );
      },
    },
  ]);

  return (
    <Box width={"100%"}>
      <Box height={"calc(100vh - 22rem)"}>
        <CustomDataGridV2
          loading={tableDataLoading || isFetching}
          searchString={searchKey}
          rowCount={UsersList?.data?.page?.totalElements}
          getRowId={(row) => row.uuid}
          rows={UsersList?.data?.content ?? []}
          columns={COLUMNS}
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          onSortModelChange={setGridSorting}
          disableRowSelectionOnClick
        />
      </Box>
      <DeleteModal
        w="380px"
        open={deleteDisc.opened}
        onClose={deleteDisc.close}
        id={deleteDisc.deleteId}
        onConfirmation={(id) =>
          archiveHandler(!archive, id, params?.providerGroupId).then((res) => {
            refetch();
            return res;
          })
        }
        content={`You want to ${archive ? "unarchive" : "archive"} this User`}
      />

      <SuccessModal
        label="The invitation link has been sent successfully to your email."
        opened={openedResendLink}
        onClose={closeResendLink}
      />
      <UserProfileDrawer
        disClosureHost={staffUserDisclosure}
        userId={(selectedUserData as any)?.uuid}
        dialogTitle="User Profile"
      />
      <UpsertStaffUserDrawer
        open={openedStaffUserModal}
        modalTitle="Edit Staff User"
        onClose={() => {
          closeStaffUserModal();
          setSelectedUserId(undefined);
        }}
        initialValues={{
          ...(userDetail?.data ?? {}),
          workLocations: userDetail?.data?.workLocations
            ? transformMultiSelectFrontEndVal(
                DEFAULT_VALUE_LABEL_LIST,
                userDetail?.data?.workLocations
              )
            : undefined,
        }}
        onSubmit={editStaffUser}
      />
    </Box>
  );
};

export default ProviderGroupStaffUsers;
